// Site of administration — HL7 table 0163, as the backend accepts it.
//
// `RXR.2` carries these, and `HealthlinkTable0163.getLabelByValue` returns null for
// anything it does not recognise, so an unrecognised value goes out half-populated
// rather than failing loudly — the same shape of problem as the route (KIN-1680).
//
// The picker used to offer six of the fifty-six codes the backend supports, which is
// why the legacy gluteal and deltoid values had nothing to heal to. The full set is
// offered now, with the six that vaccination actually uses first: the field is a
// searchable select, and a clinician reaching for "Left Thigh" should not have to scroll
// past a central line to find it.

/** The sites a vaccination is realistically given at. Offered first, in this order. */
const COMMON_SITE_CODES = ["LT", "RT", "LUA", "RUA", "LD", "RD", "MOUTH", "BN"];

// Every code in table 0163, labelled as the backend labels it.
const ALL_SITES = [
  { value: "BE", label: "Bilateral Ears" },
  { value: "BN", label: "Bilateral Nares" },
  { value: "BU", label: "Buttock" },
  { value: "CT", label: "Chest Tube" },
  { value: "LA", label: "Left Arm" },
  { value: "LAC", label: "Left Anterior Chest" },
  { value: "LACF", label: "Left Antecubital Fossa" },
  { value: "LD", label: "Left Deltoid" },
  { value: "LE", label: "Left Ear" },
  { value: "LEJ", label: "Left External Jugular" },
  { value: "LF", label: "Left Foot" },
  { value: "LG", label: "Left Gluteus Medius" },
  { value: "LH", label: "Left Hand" },
  { value: "LIJ", label: "Left Internal Jugular" },
  { value: "LLAQ", label: "Left Lower Abd Quadrant" },
  { value: "LLFA", label: "Left Lower Forearm" },
  { value: "LMFA", label: "Left Mid Forearm" },
  { value: "LN", label: "Left Naris" },
  { value: "LPC", label: "Left Posterior Chest" },
  { value: "LSC", label: "Left Subclavian" },
  { value: "LT", label: "Left Thigh" },
  { value: "LUA", label: "Left Upper Arm" },
  { value: "LUAQ", label: "Left Upper Abd Quadrant" },
  { value: "LUFA", label: "Left Upper Forearm" },
  { value: "LVG", label: "Left Ventragluteal" },
  { value: "LVL", label: "Left Vastus Lateralis" },
  { value: "MOUTH", label: "Mouth" },
  { value: "NB", label: "Nebulized" },
  { value: "OD", label: "Right Eye" },
  { value: "OS", label: "Left Eye" },
  { value: "OU", label: "Bilateral Eyes" },
  { value: "PA", label: "Perianal" },
  { value: "PERIN", label: "Perineal" },
  { value: "RA", label: "Right Arm" },
  { value: "RAC", label: "Right Anterior Chest" },
  { value: "RACF", label: "Right Antecubital Fossa" },
  { value: "RD", label: "Right Deltoid" },
  { value: "RE", label: "Right Ear" },
  { value: "REJ", label: "Right External Jugular" },
  { value: "RF", label: "Right Foot" },
  { value: "RG", label: "Right Gluteus Medius" },
  { value: "RH", label: "Right Hand" },
  { value: "RIJ", label: "Right Internal Jugular" },
  { value: "RLAQ", label: "Rt Lower Abd Quadrant" },
  { value: "RLFA", label: "Right Lower Forearm" },
  { value: "RMFA", label: "Right Mid Forearm" },
  { value: "RN", label: "Right Naris" },
  { value: "RPC", label: "Right Posterior Chest" },
  { value: "RSC", label: "Right Subclavian" },
  { value: "RT", label: "Right Thigh" },
  { value: "RUA", label: "Right Upper Arm" },
  { value: "RUAQ", label: "Right Upper Abd Quadrant" },
  { value: "RUFA", label: "Right Upper Forearm" },
  { value: "RVG", label: "Right Ventragluteal" },
  { value: "RVL", label: "Right Vastus Lateralis" },
];

const byLabel = (first, second) => first.label.localeCompare(second.label);

export const SITE_OPTIONS = [
  ...COMMON_SITE_CODES.map((code) =>
    ALL_SITES.find((site) => site.value === code)
  ),
  ...ALL_SITES.filter((site) => !COMMON_SITE_CODES.includes(site.value)).sort(
    byLabel
  ),
];

/**
 * Legacy site values, mapped to their 0163 codes.
 *
 * Only the unambiguous ones. Deliberately absent, because each would require asserting
 * something the record does not say:
 *
 * - `Left shoulder` / `Right shoulder` — the laterality is known but the structure is
 *   not: 0163 has no shoulder, and both Deltoid and Upper Arm are defensible readings.
 * - `Left leg` / `Right leg` — 0163 has thigh and foot, not leg.
 * - `Left buttock` / `Right buttock`, `Left gluteal` / `Right gluteal` — `BU` is the
 *   region but unsided, `LG`/`RG` name the gluteus medius specifically. Choosing either
 *   discards or invents information.
 * - `deltoid`, `arm`, `shoulder`, `thigh`, `back`, `upper back`, `anterolateral thigh` —
 *   no laterality at all, and 0163 has no unsided limb code.
 *
 * Those keep the existing behaviour: shown as they are on an edit, and left alone.
 * Guessing a side in a clinical record is not a decision this mapping should make.
 */
export const LEGACY_SITE_GIVEN_CODES = {
  Oral: "MOUTH",
  rotarix: "MOUTH",
  "Left thigh": "LT",
  "Right thigh": "RT",
  "Left Deltoid": "LD",
  "Left deltoid muscle": "LD",
  "Right Deltoid": "RD",
  "Right deltoid muscle": "RD",
  "Left arm": "LA",
  "Right arm": "RA",
};
